import { I18nAndL10n } from '@application/i18n/i18n_and_l10n';
import { UniversalConfig } from '@application/universal_config.service';
import { Injectable, Scopes } from '@visiba/instantiation';
import { Logger } from '@visiba/logger';
import { applicationLocalStorage } from '../../application/application_storage_local';

// import { Singleton } from '@visiba/instantiation';
// import { applicationLocalStorage } from '../application_local_storage';
// import { LicenceHolderService } from './licence_holder.service';

// TODO: DEV-10753 Find a new home for these interfaces.
interface MigrationVerifier {
	readonly migrationVersion: number;
}
interface DataIteration_v0 {
	readonly selectedLanguage: I18nAndL10n.AvailableVisibaLocales;
}

export type Settings = MigrationVerifier & DataIteration_v0;

export class UserSettingsNotSetError extends Error {
	constructor(public message: string) {
		super(message);
		this.name = 'UserSettingsNotSetError';
		Object.setPrototypeOf(this, UserSettingsNotSetError.prototype);
	}
}

@Injectable({
	scope: Scopes.Singleton,
})
export class UserSettingsService {
	private static readonly storageKey = 'USER_SETTINGS';
	private readonly activeMigrationVersion = 0;
	private storedSettings: Settings | null = null;
	private logger = new Logger('user settings service');

	public getSettings(): Omit<Settings, 'migrationVersion'>;
	public getSettings(failGracefully: boolean): Omit<Settings, 'migrationVersion'> | null;
	public getSettings(failGracefully?: boolean): Omit<Settings, 'migrationVersion'> | null {
		let settings: Settings;
		if (this.storedSettings == null) {
			if (failGracefully) return null;

			settings = this.loadSettings();
		} else {
			settings = this.storedSettings;
		}

		return settings;
	}

	constructor(private readonly universalConfig: UniversalConfig) {
		// Empty
	}

	public loadSettings(): Settings {
		const data = applicationLocalStorage.get(UserSettingsService.storageKey);
		if (data == null) {
			this.storedSettings = this.createNewSettings();
			this.save(this.storedSettings);

			return this.storedSettings;
		}
		const newSettings = { ...data };

		// Validate languageCode of storedSettings
		const availableLanguages = this.universalConfig.availableLanguages.map((languageCode) => this.tryToSelectLanguage(languageCode));
		if (!availableLanguages.includes(data.selectedLanguage)) {
			this.logger.logWarning('Invalid languageCode in storedSettings: {selectedLanguage}', data.selectedLanguage);
			newSettings.selectedLanguage = this.retrieveSelectedLanguage();
		}

		this.storedSettings = newSettings;
		this.updateSettings(newSettings);

		return newSettings;
	}

	public updateSettings(settings: Partial<Omit<Settings, 'migrationVersion'>>): void {
		// Casting this as the actual data structure it's store as
		const currentSettings = this.getSettings() as Settings;

		// TODO: DEV-10753 Add migration logic
		// if (this.activeMigrationVersion !== currentSettings.migrationVersion) {
		// 	const migration = new MigrationStrategyPipe(currentSettings);
		// 	this.storedSettings = migration.migrate(settings);

		// 	return;
		// }

		const builder: Settings = {
			...currentSettings,
			...settings,
		};

		this.save(builder);
		this.storedSettings = builder;
	}

	public setLanguage(): void {
		const { selectedLanguage } = this.getSettings();
		this.logger.logInformation('Sets language to {selectedLanguage}', selectedLanguage);

		this.universalConfig.selectedLanguageCode.value = selectedLanguage;
		I18nAndL10n.updateLangCode(selectedLanguage);
	}

	private save(data: Settings): void {
		applicationLocalStorage.set(UserSettingsService.storageKey, data);
	}

	private createNewSettings(): Settings {
		return {
			migrationVersion: this.activeMigrationVersion,
			selectedLanguage: this.retrieveSelectedLanguage(),
		};
	}

	private tryToSelectLanguage(str: string): I18nAndL10n.AvailableVisibaLocales | null {
		try {
			return I18nAndL10n.convertExternalLocaleToAvailableLocale(str);
		} catch (_: unknown) {
			return null;
		}
	}

	private retrieveSelectedLanguage(): I18nAndL10n.AvailableVisibaLocales {
		// TODO: DEV-10753 Remove this in favour of migration logic
		const OLD_PATIENT_WEB_LANG = localStorage.getItem('languageCode') as string | null;

		const languagesToTry = new Set([OLD_PATIENT_WEB_LANG, navigator.language, ...navigator.languages].filter(Boolean) as string[]);

		let selectedLanguage = this.universalConfig.selectedLanguageCode.value;
		const availableLanguages = this.universalConfig.availableLanguages.map((languageCode) => this.tryToSelectLanguage(languageCode));
		for (const language of languagesToTry) {
			const selectedLanguageCandidate = this.tryToSelectLanguage(language);
			if (selectedLanguageCandidate != null && availableLanguages.includes(selectedLanguageCandidate)) {
				selectedLanguage = selectedLanguageCandidate;
				break;
			}
		}

		return selectedLanguage as I18nAndL10n.AvailableVisibaLocales;
	}
}
